import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useCompleteChecklistItem } from '@/hooks/useOnboarding';
import { useAuth } from '@/hooks/useAuth';
import { useOverlayOpen } from '@/hooks/useOverlayOpen';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { Icon } from '@/components/ui/Icon';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { AnyChecklistItemId } from '@/services/onboarding.service';

/**
 * FloatingChecklist Component
 *
 * Floating widget showing onboarding progress and checklist items.
 * Positioned fixed at bottom-right corner of the screen.
 *
 * Features:
 * - Collapsed state: Small pill showing progress (e.g., "2/5")
 * - Expanded state: Full checklist with items and actions
 * - Smooth expand/collapse animation
 * - Dismiss button with confirmation
 * - Auto-hides when showChecklist is false
 * - Renders via portal to avoid z-index issues
 */

interface ChecklistConfigItem {
  id: AnyChecklistItemId;
  labelKey: string;
  action: string;
  requiresAdmin: boolean;
  requiresWhitelabel?: boolean;
}

const CHECKLIST_CONFIG: ChecklistConfigItem[] = [
  {
    id: 'connect_channel',
    labelKey: 'onboarding_checklist_item_connect_channel',
    action: 'openChannelModal',
    requiresAdmin: false,
  },
  {
    id: 'create_calendar',
    labelKey: 'onboarding_checklist_item_create_calendar',
    action: 'openCalendarModal',
    requiresAdmin: false,
  },
  {
    id: 'create_post',
    labelKey: 'onboarding_checklist_item_create_post',
    action: 'openPostCreator',
    requiresAdmin: false,
  },
  {
    id: 'schedule_post',
    labelKey: 'onboarding_checklist_item_schedule_post',
    action: 'openPostCreator',
    requiresAdmin: false,
  },
  {
    id: 'explore_calendar',
    labelKey: 'onboarding_checklist_item_explore_calendar',
    action: 'navigateCalendar',
    requiresAdmin: false,
  },
  {
    id: 'invite_team_member',
    labelKey: 'onboarding_checklist_item_invite_team_member',
    action: 'openInviteModal',
    requiresAdmin: true, // Only show for OWNER or ADMIN
  },
];

const CLIENT_CHECKLIST_CONFIG: ChecklistConfigItem[] = [
  {
    id: 'create_draft',
    labelKey: 'onboarding_checklist_item_create_draft',
    action: 'openPostCreator',
    requiresAdmin: false,
  },
  {
    id: 'explore_dashboard',
    labelKey: 'onboarding_checklist_item_explore_dashboard',
    action: 'navigateDashboard',
    requiresAdmin: false,
  },
  {
    id: 'browse_calendar',
    labelKey: 'onboarding_checklist_item_browse_calendar',
    action: 'navigateCalendar',
    requiresAdmin: false,
  },
  {
    id: 'review_approval_post',
    labelKey: 'onboarding_checklist_item_review_approval_post',
    action: 'navigateApprovals',
    requiresAdmin: false,
  },
  {
    id: 'approve_post',
    labelKey: 'onboarding_checklist_item_approve_post',
    action: 'navigateApprovals',
    requiresAdmin: false,
  },
  {
    id: 'leave_comment',
    labelKey: 'onboarding_checklist_item_leave_comment',
    action: 'navigateApprovalsComment',
    requiresAdmin: false,
  },
];

const AGENCY_CHECKLIST_CONFIG: ChecklistConfigItem[] = [
  {
    id: 'create_client_workspace',
    labelKey: 'onboarding_checklist_item_create_client_workspace',
    action: 'openCreateWorkspace',
    requiresAdmin: false,
  },
  {
    id: 'invite_staff_member',
    labelKey: 'onboarding_checklist_item_invite_staff_member',
    action: 'openInviteTeamMember',
    requiresAdmin: false,
  },
  {
    id: 'setup_branding',
    labelKey: 'onboarding_checklist_item_setup_branding',
    action: 'navigateAgencyBranding',
    requiresAdmin: false,
    requiresWhitelabel: true, // Only show when agency has Atelier Mode (whitelabel)
  },
  {
    id: 'connect_client_channel',
    labelKey: 'onboarding_checklist_item_connect_client_channel',
    action: 'navigateAgencyPortfolio',
    requiresAdmin: false,
  },
  {
    id: 'create_client_calendar',
    labelKey: 'onboarding_checklist_item_create_client_calendar',
    action: 'navigateAgencyPortfolio',
    requiresAdmin: false,
  },
  {
    id: 'customize_agency_profile',
    labelKey: 'onboarding_checklist_item_customize_agency_profile',
    action: 'navigateAgencySettings',
    requiresAdmin: false,
  },
];

export function FloatingChecklist() {
  const { t } = useTranslation();
  const { showChecklist, showWizard, variant, progress, dismissChecklist, refetchProgress } = useOnboarding();
  const { user } = useAuth();
  const { isInGodMode } = useEffectiveTeamId();
  const navigate = useNavigate();
  // Explicit user choice (persisted). `null` = no saved preference -> derive the default below.
  const [userChoice, setUserChoice] = useState<boolean | null>(() => {
    const stored = localStorage.getItem('onboarding-checklist-expanded');
    return stored !== null ? stored === 'true' : null;
  });
  // No saved preference: open on desktop (except for agency staff, whose settings / drawer
  // controls sit bottom-right; derived live because the user may hydrate after first mount);
  // collapsed below lg so it never covers the content / bottom nav on first load.
  const defaultExpanded =
    !user?.isAgencyStaff &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(min-width: 1024px)').matches;
  const isExpanded = userChoice ?? defaultExpanded;
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const overlayOpen = useOverlayOpen();
  // On the portfolio the FAB owns the bottom (centre slot on mobile, right on desktop):
  // below lg the checklist collapses to a compact progress ring at the bottom-left so every
  // onboarding step stays reachable; on desktop it sits beside the FAB.
  const { pathname, search } = useLocation();
  const besideFab = pathname.startsWith('/agency/portfolio');
  const isUpgradeFlow = pathname === '/upgrade' || pathname.startsWith('/upgrade/') || pathname === '/shop' || pathname === '/shop/checkout' || pathname === '/settings/whitelabel' || pathname === '/agency/settings/whitelabel' || ((pathname === '/settings' || pathname === '/agency/settings') && new URLSearchParams(search).get('tab') === 'billing');
  const {
    mutate: completeChecklistItemMutate,
    isPending: isCompletingItem,
    variables: pendingItemId,
  } = useCompleteChecklistItem();

  // Refresh progress whenever the widget is expanded, so backend
  // auto-completions (and manual check-offs made elsewhere) show up
  // without requiring a hard reload.
  useEffect(() => {
    if (isExpanded) {
      refetchProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  // Select checklist config based on API variant (source of truth)
  const isAdminOrOwner = user?.teamRole === 'OWNER' || user?.teamRole === 'ADMIN';
  const hasWhitelabel = user?.agency?.whitelabelEnabled === true;
  const baseConfig =
    variant === 'agency'
      ? AGENCY_CHECKLIST_CONFIG
      : variant === 'client'
      ? CLIENT_CHECKLIST_CONFIG
      : CHECKLIST_CONFIG;
  const visibleItems = useMemo(() => {
    return baseConfig.filter(item => {
      if (item.requiresAdmin && !isAdminOrOwner) return false;
      if (item.requiresWhitelabel && !hasWhitelabel) return false;
      return true;
    });
  }, [isAdminOrOwner, hasWhitelabel, baseConfig]);

  // Don't render if checklist should not be shown, wizard is active, or in God Mode
  // The dismiss ConfirmDialog below is gated by the same conditions, but NEVER by overlayOpen: its own Radix scroll
  // lock makes overlayOpen true, and hiding the dialog because of it unmounts it, which drops the lock, which
  // remounts it (an endless mount loop that froze the tab). Only the floating panel hides while an overlay is open.
  if (!showChecklist || showWizard || isInGodMode || isUpgradeFlow) return null;

  // Calculate progress based on visible items only
  const checklistItems = progress?.checklistItems ?? {};
  const completedCount = visibleItems.filter(item => checklistItems[item.id]).length;
  const totalCount = visibleItems.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  // Handle expand/collapse toggle
  const handleToggle = () => {
    const newState = !isExpanded;
    setUserChoice(newState);
    localStorage.setItem('onboarding-checklist-expanded', String(newState));
  };

  // Handle checklist item action
  const handleAction = (action: string) => {
    switch (action) {
      case 'openChannelModal':
        window.dispatchEvent(new CustomEvent('open-channel-modal'));
        break;
      case 'openCalendarModal':
        window.dispatchEvent(new CustomEvent('open-calendar-modal'));
        break;
      case 'openPostCreator':
        window.dispatchEvent(new CustomEvent('open-post-creator'));
        break;
      case 'navigateDashboard':
        navigate('/');
        break;
      case 'navigateCalendar':
        navigate('/calendar');
        break;
      case 'navigateApprovals':
        navigate('/approvals');
        break;
      case 'navigateApprovalsComment':
        navigate('/approvals?openFirst=true&tab=comments');
        break;
      case 'openCampaignModal':
        navigate('/calendar');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('open-campaign-modal'));
        }, 100);
        break;
      case 'openInviteModal':
        window.dispatchEvent(new CustomEvent('open-invite-modal'));
        break;
      case 'openCreateWorkspace':
        navigate('/agency/portfolio?createBrand=1');
        break;
      case 'navigateAgencyPortfolio':
        navigate('/agency/portfolio');
        break;
      case 'openInviteTeamMember':
        navigate('/agency/settings?tab=team&addMember=1');
        break;
      case 'navigateAgencySettings':
        navigate('/agency/settings');
        break;
      case 'navigateAgencyBranding':
        navigate('/agency/settings/whitelabel');
        break;
    }
  };

  // Handle manual check-off of an incomplete item
  const handleMarkComplete = (itemId: AnyChecklistItemId) => {
    completeChecklistItemMutate(itemId);
  };

  // Handle dismiss
  const handleDismiss = () => {
    setShowDismissConfirm(true);
  };

  const handleConfirmDismiss = async () => {
    await dismissChecklist();
    setShowDismissConfirm(false);
  };

  const content = (
    <div
      className={cn(
        // z-30: floating widgets stay below sidebar/navbar, the FAB (z-40) and all modals (z-modal-*)
        'fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+10px+var(--floating-stack-offset))] z-30 lg:bottom-6',
        besideFab
          ? 'left-3 sm:left-4 lg:left-auto lg:right-[calc(1.5rem+var(--floating-fab-w))]'
          : 'right-3 lg:right-6'
      )}
      role="region"
      aria-label={t('onboarding_checklist', { defaultValue: 'Onboarding checklist' })}
    >
      {/* Collapsed State - Progress Pill */}
      {!isExpanded && (
        <button
          onClick={handleToggle}
          className={cn(
            'flex items-center gap-3 px-4 py-3 bg-white rounded-full shadow-lg border border-gray-200',
            besideFab && 'max-lg:p-1.5 max-lg:gap-0',
            'hover:shadow-xl hover:scale-105 transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
          )}
          aria-label={`Open checklist - ${completedCount} of ${totalCount} complete`}
        >
          {/* Progress Ring */}
          <div className="relative w-10 h-10">
            <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="3"
              />
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="var(--color-primary-500)"
                strokeWidth="3"
                strokeDasharray={`${progressPercent} 100`}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">
              {completedCount}/{totalCount}
            </span>
          </div>
          <div className={cn('pr-1', besideFab && 'hidden lg:block')}>
            <p className="text-sm font-semibold text-gray-900">{t('onboarding_get_started')}</p>
            <p className="text-xs text-gray-500">{t('onboarding_progress', { percent: progressPercent })}</p>
          </div>
          <span className={cn(besideFab && 'hidden lg:inline-flex')}>
            <Icon name={ICONS.ANGLE_UP} size="sm" color="text-gray-400" ariaHidden />
          </span>
        </button>
      )}

      {/* Expanded State - Full Checklist */}
      {isExpanded && (
        <div
          className={cn(
            'w-[min(20rem,calc(100vw-1.5rem))] bg-white rounded-2xl shadow-2xl border border-gray-200',
            'animate-in fade-in slide-in-from-bottom-4 duration-200'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              {/* Progress Ring */}
              <div className="relative w-10 h-10">
                <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="3"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    fill="none"
                    stroke="var(--color-primary-500)"
                    strokeWidth="3"
                    strokeDasharray={`${progressPercent} 100`}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">
                  {completedCount}/{totalCount}
                </span>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">{t('onboarding_get_started')}</h3>
                <p className="text-xs text-gray-500">{t('onboarding_progress', { percent: progressPercent })}</p>
              </div>
            </div>

            <button
              onClick={handleToggle}
              className="w-[44px] h-[44px] lg:w-7 lg:h-7 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              aria-label={t('collapse_checklist', { defaultValue: 'Collapse checklist' })}
              type="button"
            >
              <Icon name={ICONS.ANGLE_DOWN} size="sm" color="text-gray-500" ariaHidden />
            </button>
          </div>

          {/* Checklist Items */}
          <div className="max-h-[min(20rem,calc(100dvh-340px))] overflow-y-auto">
            {visibleItems.map((item) => {
              const completed = checklistItems[item.id] ?? false;
              const itemLabel = t(item.labelKey);
              return completed ? (
                <div
                  key={item.id}
                  className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-b-0 opacity-60"
                >
                  <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                    <Icon name={ICONS.CHECK} size="xs" color="text-white" ariaHidden />
                  </div>
                  <span className="text-sm text-gray-400 line-through">
                    {itemLabel}
                  </span>
                </div>
              ) : (
                <div
                  key={item.id}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-b-0',
                    'hover:bg-gray-50 transition-colors'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => handleMarkComplete(item.id)}
                    disabled={isCompletingItem && pendingItemId === item.id}
                    aria-label={t('onboarding_mark_done_aria', {
                      title: itemLabel,
                      defaultValue: `Mark '${itemLabel}' as done`,
                    })}
                    className={cn(
                      'w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0 transition-colors',
                      'hover:border-primary-500 hover:bg-primary-50',
                      'focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1',
                      isCompletingItem && pendingItemId === item.id && 'opacity-50 cursor-wait'
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => handleAction(item.action)}
                    className="flex-1 flex items-center justify-between gap-3 text-left min-w-0"
                  >
                    <span className="text-sm text-gray-700 truncate">{itemLabel}</span>
                    <Icon name={ICONS.ARROW_RIGHT} size="sm" color="text-primary-600" ariaHidden />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <button
              onClick={handleDismiss}
              className="w-full text-center text-xs text-gray-500 hover:text-gray-700 py-3 lg:py-1 min-h-[44px] lg:min-h-0 transition-colors"
              type="button"
            >
              {t('onboarding_dismiss')}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Render via portal to avoid z-index issues
  return (
    <>
      {/* Floating widgets sit below modals/drawers: hide the panel (not the dialog) while any overlay is open. */}
      {!overlayOpen && createPortal(content, document.body)}
      <ConfirmDialog
        open={showDismissConfirm}
        onOpenChange={setShowDismissConfirm}
        title={t('checklist_dismiss_title', { defaultValue: 'Dismiss Checklist' })}
        description={t('checklist_dismiss_desc', { defaultValue: 'Are you sure you want to dismiss the checklist? You can re-enable it from settings.' })}
        confirmText={t('dismiss', { defaultValue: 'Dismiss' })}
        cancelText={t('keep_it', { defaultValue: 'Keep It' })}
        variant="warning"
        onConfirm={handleConfirmDismiss}
      />
    </>
  );
}
